// Shared helpers for OrderX V4 (frontend/src/pages/OrderXV4.js and its step components).

import * as XLSX from 'xlsx';
import { getAuthUserFromStorage } from '../utils/authHeaders';

// Tracks the most recent in-flight/background request per browser so the user can
// close the tab and come back later (see OrderXV4.js "resume pending request" banner)
// without needing the completion email to be the only way back in.
const PENDING_REQUEST_STORAGE_KEY = 'order_genie_v4_pending_request';

export const getStoredPendingRequest = () => {
  try {
    const raw = localStorage.getItem(PENDING_REQUEST_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.requestId || !parsed?.userId) return null;
    return parsed;
  } catch (err) {
    return null;
  }
};

export const setStoredPendingRequest = (requestId, userId, requestName) => {
  try {
    localStorage.setItem(PENDING_REQUEST_STORAGE_KEY, JSON.stringify({
      requestId, userId, requestName: requestName || '', startedAt: new Date().toISOString(),
    }));
  } catch (err) { /* ignore storage errors (private browsing, quota, etc.) */ }
};

export const clearStoredPendingRequest = () => {
  try { localStorage.removeItem(PENDING_REQUEST_STORAGE_KEY); } catch (err) { /* ignore */ }
};

const LAST_CUSTOMER_STORAGE_KEY = 'order_genie_v4_last_customer';
const LAST_SHIP_TO_STORAGE_KEY = 'order_genie_v4_last_ship_to';
const LAST_CHECKOUT_STORAGE_KEY = 'order_genie_v4_last_checkout';

const readJsonStorage = (key) => {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch (err) {
    return null;
  }
};

export const formatCustomerLabel = (customer) => {
  if (!customer) return '';
  const id = String(customer.erpCustomerId || '').trim();
  const name = String(customer.customerName || '').trim();
  if (id && name) return `${id} — ${name}`;
  return id || name;
};

export const formatShipToLabel = (shipTo) => {
  if (!shipTo) return '';
  const id = String(shipTo.shipToId || '').trim();
  const company = String(shipTo.shipToAddress?.ShipToCompanyName || '').trim();
  if (id && company) return `${id} — ${company}`;
  return id || company;
};

const needsLabelUpgrade = (search, id, label) => {
  const current = String(search || '').trim();
  const erpId = String(id || '').trim();
  const next = String(label || '').trim();
  if (!next) return false;
  return !current || current === erpId;
};

/** Restore last customer/ship-to with full address/contact details (not just IDs). */
export const getStoredLastCheckout = () => {
  const stored = readJsonStorage(LAST_CHECKOUT_STORAGE_KEY) || {};
  let lastCustomer = '';
  let lastShipTo = '';
  try {
    lastCustomer = localStorage.getItem(LAST_CUSTOMER_STORAGE_KEY) || '';
    lastShipTo = localStorage.getItem(LAST_SHIP_TO_STORAGE_KEY) || '';
  } catch (err) { /* ignore */ }
  const customer = String(stored.customer || lastCustomer || '').trim();
  const shipTo = String(stored.ship_to || lastShipTo || '').trim();
  const customerDetails = stored.customer_details || null;
  const shipToDetails = stored.ship_to_details || null;
  let customerSearch = String(stored.customer_search || customer).trim();
  let shipToSearch = String(stored.ship_to_search || shipTo).trim();

  const customerLabel = formatCustomerLabel(customerDetails);
  const shipToLabel = formatShipToLabel(shipToDetails);
  let shouldRewrite = false;
  if (needsLabelUpgrade(customerSearch, customer, customerLabel)) {
    customerSearch = customerLabel;
    shouldRewrite = true;
  }
  if (needsLabelUpgrade(shipToSearch, shipTo, shipToLabel)) {
    shipToSearch = shipToLabel;
    shouldRewrite = true;
  }

  const checkout = {
    customer,
    customer_search: customerSearch,
    customer_details: customerDetails,
    ship_to: shipTo,
    ship_to_search: shipToSearch,
    ship_to_details: shipToDetails,
    bill_to_details: stored.bill_to_details || null,
    contact: String(stored.contact || '').trim(),
    contact_details: stored.contact_details || null,
    shipping_method: String(stored.shipping_method || '').trim(),
  };
  if (shouldRewrite) setStoredLastCheckout(checkout);
  return checkout;
};

/** Persist last checkout selection including address payloads used by create-order. */
export const setStoredLastCheckout = (formValues) => {
  const customer = String(formValues?.customer || '').trim();
  const shipTo = String(formValues?.ship_to || '').trim();
  const customerDetails = formValues?.customer_details || null;
  const shipToDetails = formValues?.ship_to_details || null;
  let customerSearch = String(formValues?.customer_search || customer || '').trim();
  let shipToSearch = String(formValues?.ship_to_search || shipTo || '').trim();
  const customerLabel = formatCustomerLabel(customerDetails);
  const shipToLabel = formatShipToLabel(shipToDetails);
  if (needsLabelUpgrade(customerSearch, customer, customerLabel)) {
    customerSearch = customerLabel;
  }
  if (needsLabelUpgrade(shipToSearch, shipTo, shipToLabel)) {
    shipToSearch = shipToLabel;
  }
  const payload = {
    customer,
    customer_search: customerSearch,
    customer_details: customerDetails,
    ship_to: shipTo,
    ship_to_search: shipToSearch,
    ship_to_details: shipToDetails,
    bill_to_details: formValues?.bill_to_details || null,
    contact: String(formValues?.contact || '').trim(),
    contact_details: formValues?.contact_details || null,
    shipping_method: String(formValues?.shipping_method || '').trim(),
  };
  try {
    localStorage.setItem(LAST_CHECKOUT_STORAGE_KEY, JSON.stringify(payload));
    if (payload.customer) localStorage.setItem(LAST_CUSTOMER_STORAGE_KEY, payload.customer);
    if (payload.ship_to) localStorage.setItem(LAST_SHIP_TO_STORAGE_KEY, payload.ship_to);
  } catch (err) { /* ignore storage errors (private browsing, quota, etc.) */ }
};

// Maps backend process-status stage/step keys to a user-facing label. The backend
// (order_genie_service run_process_job) already sends a human `message`/`detail`
// pair for the current phase — this is only a fallback for older/unlabeled states.
export const STAGE_FALLBACK_LABELS = {
  queued: 'Queued for processing…',
  reading: 'Reading your document…',
  parsing: 'Extracting line items…',
  processing: 'Extracting line items…',
  parsed: 'Line items extracted…',
  matching: 'Matching products against the catalog…',
  ai_rerank: 'Refining low-confidence matches with AI…',
  rerank: 'Refining low-confidence matches with AI…',
  reranking: 'Refining low-confidence matches with AI…',
  completed: 'Ready for review',
  matched: 'Ready for review',
};

export const P21_SHIPPING_METHODS = [
  { id: '132620', label: 'BEST WAY' },
  { id: '100000', label: 'UPS Ground' },
  { id: '100001', label: 'UPS Next Day Air' },
  { id: '100002', label: 'UPS 2nd Day Air' },
  { id: '120902', label: 'Will Call' },
  { id: '117662', label: 'UPS Collect' },
];

export const DEFAULT_SHIPPING_METHODS = P21_SHIPPING_METHODS.map((method) => method.label);

// Eclipse ship-via codes (id = ShipViaID sent on create-order).
export const ECLIPSE_SHIPPING_METHODS = [
  { id: 'OT OUR TRUCK', label: 'VAMAC Truck' },
  { id: 'UPS UPS GROUND', label: 'UPS GROUND' },
  { id: 'U2D UPS 2ND DAY', label: 'UPS 2nd Day' },
  { id: 'UND UPS NXT DAY', label: 'UPS Next Day' },
  { id: 'PU PICK-UP', label: 'Customer Pickup' },
  { id: 'CS CURBSIDE', label: 'Curbside' },
  { id: 'WC WILL CALL', label: 'Will Call' },
];

const isEclipseConnector = (erpConnector) => String(erpConnector || '').toLowerCase() === 'eclipse';

// `eclipseMethods`: live shipViaInquiry list when loaded; falls back to the static codes.
export const getShippingMethodsForConnector = (erpConnector, eclipseMethods = null) => {
  if (!isEclipseConnector(erpConnector)) return P21_SHIPPING_METHODS;
  return eclipseMethods?.length ? eclipseMethods : ECLIPSE_SHIPPING_METHODS;
};

const P21_CARRIER_ALIASES = {
  ground: '100000',
  '2-day air': '100002',
  overnight: '100001',
  'freight/ltl': '132620',
  'will call/pickup': '120902',
  pickup: '120902',
};

export const resolveP21CarrierId = (shippingMethod) => {
  const raw = String(shippingMethod || '').trim();
  if (!raw) return '';
  if (P21_SHIPPING_METHODS.some((method) => method.id === raw)) return raw;
  const key = raw.toLowerCase();
  const byLabel = P21_SHIPPING_METHODS.find((method) => method.label.toLowerCase() === key);
  if (byLabel) return byLabel.id;
  return P21_CARRIER_ALIASES[key] || '';
};

export const getP21ShippingMethodLabel = (shippingMethod) => {
  const raw = String(shippingMethod || '').trim();
  if (!raw) return '';
  const byId = P21_SHIPPING_METHODS.find((method) => method.id === raw);
  if (byId) return byId.label;
  const byLabel = P21_SHIPPING_METHODS.find(
    (method) => method.label.toLowerCase() === raw.toLowerCase()
  );
  return byLabel ? byLabel.label : raw;
};

// Connector-aware id/label lookups for the shipping method select + summary.
export const resolveShippingMethodId = (shippingMethod, erpConnector, eclipseMethods = null) => {
  if (!isEclipseConnector(erpConnector)) return resolveP21CarrierId(shippingMethod);
  const raw = String(shippingMethod || '').trim();
  if (!raw) return '';
  const key = raw.toLowerCase();
  const match = getShippingMethodsForConnector(erpConnector, eclipseMethods).find(
    (method) => method.id.toLowerCase() === key || method.label.toLowerCase() === key
  );
  return match ? match.id : '';
};

export const getShippingMethodLabel = (shippingMethod, erpConnector, eclipseMethods = null) => {
  if (!isEclipseConnector(erpConnector)) return getP21ShippingMethodLabel(shippingMethod);
  const id = resolveShippingMethodId(shippingMethod, erpConnector, eclipseMethods);
  const match = getShippingMethodsForConnector(erpConnector, eclipseMethods).find((method) => method.id === id);
  return match ? match.label : String(shippingMethod || '').trim();
};

const pickFirstStr = (...values) => {
  for (const value of values) {
    const text = String(value ?? '').trim();
    if (text) return text;
  }
  return '';
};

/** Build P21 CustomerShipTo from checkout form state (ship-to + contact + token fallbacks). */
export const buildCustomerShipToPayload = (formValues, shippingMethod, tokenUser) => {
  const shipToId = String(formValues?.ship_to || '').trim();
  if (!shipToId) return null;

  const shipDetails = formValues?.ship_to_details || {};
  const contactDetails = formValues?.contact_details || {};
  const user = tokenUser || getAuthUserFromStorage();
  const shipToAddress = shipDetails.shipToAddress || {
    ShipToAddress1: '',
    ShipToCity: '',
    ShipToState: '',
    ShipToZip: '',
    ShipToCountry: 'US',
  };

  return {
    ShipToID: shipToId,
    ShipToAddress: shipToAddress,
    ShipToContactFirstName: pickFirstStr(
      shipDetails.ShipToContactFirstName,
      contactDetails.firstName,
      contactDetails.contactFirstName,
      user.firstName,
    ),
    ShipToContactLastName: pickFirstStr(
      shipDetails.ShipToContactLastName,
      contactDetails.lastName,
      contactDetails.contactLastName,
      user.lastName,
    ) || '.',
    ShipToPhone: pickFirstStr(shipDetails.ShipToPhone, contactDetails.phone),
    ShipToEMail: pickFirstStr(shipDetails.ShipToEMail, contactDetails.email, user.email),
    ShipToCarrierId: resolveP21CarrierId(shippingMethod) || shipDetails.ShipToCarrierId || '',
  };
};

// Fallback checkout flow config used only if the backend settings haven't loaded
// yet or don't yet include one — mirrors _DEFAULT_CHECKOUT_FLOW_CONFIG in
// order_genie_service/api/order_genie.py so the UI never breaks before an admin
// (or the backend default) provides real config.
export const DEFAULT_CHECKOUT_FLOW_CONFIG = {
  steps: {
    review_match: {
      fields: [
        { key: 'po_number', label: 'PO Number', type: 'text', visible: true, required: false },
        { key: 'required_date', label: 'Required Date', type: 'date', visible: true, required: false },
        { key: 'project_name', label: 'Project Name', type: 'text', visible: true, required: false },
        { key: 'order_notes', label: 'Order Notes', type: 'textarea', visible: true, required: false },
      ],
    },
    customer_shipping: {
      fields: [
        { key: 'customer', label: 'Customer', type: 'customer_picker', visible: true, required: true },
        {
          key: 'ship_to',
          label: 'Ship To',
          type: 'shipto_picker',
          visible: true,
          required_when: { all: [{ field: 'order_type', op: 'eq', value: 'order' }] },
        },
        {
          key: 'contact',
          label: 'Contact',
          type: 'contact_picker',
          visible: true,
          required: false,
        },
        {
          key: 'shipping_method',
          label: 'Shipping Method',
          type: 'select',
          options: DEFAULT_SHIPPING_METHODS,
          visible: true,
          required_when: { all: [{ field: 'order_type', op: 'eq', value: 'order' }] },
        },
      ],
    },
    summary_submit: {
      quote_required_fields: ['customer'],
      order_required_fields: ['customer', 'ship_to', 'shipping_method'],
    },
  },
};

const evaluateCondition = (condition, formValues) => {
  if (!condition || !condition.field) return true;
  const actual = formValues ? formValues[condition.field] : undefined;
  const expected = condition.value;
  if (condition.op === 'neq') return String(actual ?? '') !== String(expected ?? '');
  return String(actual ?? '') === String(expected ?? '');
};

// Evaluates a {"all": [{field, op, value}, ...]} condition group against the
// current form values. Same shape as query_flow_routes.when_clause on the
// backend (AHRI routing config) — reused here for the checkout-flow field rules.
export const evaluateFieldRule = (rule, formValues) => {
  if (rule === true || rule === false) return rule;
  if (rule == null) return true;
  if (Array.isArray(rule?.all)) {
    return rule.all.every((condition) => evaluateCondition(condition, formValues));
  }
  return true;
};

// Given a field config object ({visible, required, visible_when, required_when})
// and the current form values, returns { visible, required } booleans.
export const resolveFieldState = (field, formValues) => {
  const visible = Object.prototype.hasOwnProperty.call(field || {}, 'visible_when')
    ? evaluateFieldRule(field.visible_when, formValues)
    : field?.visible !== false;
  const required = Object.prototype.hasOwnProperty.call(field || {}, 'required_when')
    ? evaluateFieldRule(field.required_when, formValues)
    : Boolean(field?.required);
  return { visible, required };
};

export const getCheckoutFlowStep = (checkoutFlowConfig, stepKey) =>
  checkoutFlowConfig?.steps?.[stepKey] || DEFAULT_CHECKOUT_FLOW_CONFIG.steps[stepKey];

// Reads the AI-analysis flags the backend stamps on each match (order_genie_service.py
// match builders: ai_ranked, ai_analyzed, ai_low_confidence — sibling fields to `reasoning`,
// not nested under `match`). Used to badge rows so users can see which lines an AI model
// actually reviewed vs. a plain rule/exact-match, and which AI picks were low-confidence.
export const getMatchAiBadge = (match) => {
  if (!match) return null;
  if (match.ai_ranked) {
    return { label: match.ai_low_confidence ? 'AI Re-ranked · Low confidence' : 'AI Re-ranked', color: match.ai_low_confidence ? 'orange' : 'purple' };
  }
  if (match.ai_analyzed) {
    return { label: match.ai_low_confidence ? 'AI Analyzed · Low confidence' : 'AI Analyzed', color: match.ai_low_confidence ? 'orange' : 'blue' };
  }
  return null;
};

// Reads the per-tenant pricing outcome the backend attaches at match time
// (order_genie_service.py matching loop -> match_json.pricing, via
// services/pricing/registry.py get_prices_bulk). When the line item's requested
// UOM isn't among the priced UOMs returned for that product, pricing falls back
// to the first available UOM and `uom_mismatch` is flagged — surface that as a
// badge alongside getMatchAiBadge so the user knows the shown price may not be
// for the UOM they asked for.
export const getUomMismatchBadge = (match) => {
  const pricing = match?.pricing;
  if (!pricing || !pricing.uom_mismatch) return null;
  const requested = String(pricing.requested_uom || '').trim();
  const label = requested
    ? `UOM Mismatch · requested ${requested}, priced ${pricing.uom || 'N/A'}`
    : `UOM Mismatch · priced ${pricing.uom || 'N/A'}`;
  return { label, color: 'orange' };
};

// Reads the ERP-match confirmation the backend stamps on a match when it resolved
// via the ERP pricing engine fallback rather than catalog search or AI rerank (see
// order_genie_service's _try_erp_match / _build_erp_match_result). Surface as an
// independent badge alongside getMatchAiBadge/getUomMismatchBadge.
export const getErpMatchBadge = (match) => {
  const isErpMatched = match?.rule_type === 'erp_match' || Boolean(match?.debug_info?.selected?.erp_matched);
  if (!isErpMatched) return null;
  return { label: 'ERP Matched', color: 'teal' };
};

// Flags when the price extracted from the source document ("From Document")
// diverges from the effective/ERP price shown on the review-screen price card
// beyond simple rounding, so the reviewer can spot a divergence at a glance.
// Mirrors getUomMismatchBadge/getErpMatchBadge's {label, color} shape. Returns
// null when either price is missing/non-numeric or when they agree within
// tolerance. Tolerance is the greater of one cent or 1% of the input price,
// so a $0.02 difference on a $9,000 line isn't flagged while a $0.02
// difference on a $0.50 line still is. Labeled "Price Differs" rather than
// "Mismatch" since a legitimate divergence (per-tenant match pricing, ERP
// bulk pricing) is expected, not necessarily a defect.
// Story 3.4 (FR-5): every candidate is visibly labeled Base Match or Learned
// Match -- defaults to "base" when match.source is absent (every match row
// created before Story 3.3 shipped), so no candidate ever renders unlabeled.
export const getMatchSourceBadge = (match) => {
  if (!match) return null;
  if (match.source === 'learned') return { label: 'Learned Match', color: 'gold' };
  return { label: 'Base Match', color: 'default' };
};

export const getPriceDivergenceBadge = (inputPrice, effectivePrice) => {
  const hasInputPrice = Number.isFinite(Number(inputPrice)) && Number(inputPrice) > 0;
  const hasEffectivePrice = Number.isFinite(Number(effectivePrice)) && Number(effectivePrice) > 0;
  if (!hasInputPrice || !hasEffectivePrice) return null;
  const tolerance = Math.max(0.01, Number(inputPrice) * 0.01);
  const diverges = Math.abs(Number(inputPrice) - Number(effectivePrice)) > tolerance;
  if (!diverges) return null;
  return { label: 'Price Differs · Document vs ERP', color: 'orange' };
};

// Resolves the effective unit/extended price for a review-screen line item —
// shared by StepReviewMatch (display) and StepSummarySubmit (order/cart payload)
// so what the user reviews is exactly what gets submitted. Priority: per-tenant
// match pricing (set at product-match time) > ERP/dummy bulk price lookup > the
// source document's own stated unit price (only used when no real pricing engine
// is configured, i.e. pricingEngine is not p21/eclipse, and the value is a real number > 0).
// `uom` is the UOM paired with whichever price source won (match pricing UOM,
// else the ERP bulk-price UOM, else `fallbackUom`), so the displayed/submitted
// UOM always matches the unit price it describes.
export const resolveEffectiveLinePrice = ({
  matchUnitPrice, erpUnitPrice, inputUnitPrice, inputExtendedPrice, pricingEngine, qty,
  matchUom, erpUom, fallbackUom,
}) => {
  const hasMatchPrice = matchUnitPrice !== null && matchUnitPrice !== undefined && Number.isFinite(Number(matchUnitPrice));
  const hasErpPrice = Number.isFinite(Number(erpUnitPrice));
  const hasInputUnitPrice = Number.isFinite(Number(inputUnitPrice)) && Number(inputUnitPrice) > 0;
  const noRealPricingEngine = !['p21', 'eclipse'].includes(String(pricingEngine || 'dummy').trim().toLowerCase());
  const usingInputPriceFallback = !hasMatchPrice && !hasErpPrice && noRealPricingEngine && hasInputUnitPrice;

  const unitPrice = hasMatchPrice
    ? Number(matchUnitPrice)
    : hasErpPrice
      ? Number(erpUnitPrice)
      : usingInputPriceFallback
        ? Number(inputUnitPrice)
        : NaN;

  const hasInputExtendedPrice = Number.isFinite(Number(inputExtendedPrice)) && Number(inputExtendedPrice) > 0;
  const extendedPrice = usingInputPriceFallback && hasInputExtendedPrice
    ? Number(inputExtendedPrice)
    : (Number.isFinite(unitPrice) ? unitPrice * (Number(qty) || 1) : NaN);

  const pricedUom = hasMatchPrice ? matchUom : hasErpPrice ? erpUom : '';
  const uom = String(pricedUom || fallbackUom || '').trim();

  return { unitPrice, extendedPrice, usingInputPriceFallback, uom };
};

// "$5.05/pk" -- unit price with its UOM; "/ea" when no UOM is known.
export const formatUnitPrice = (value, uom) => (
  Number.isFinite(value) ? `$${value.toFixed(2)}/${String(uom || '').trim() || 'ea'}` : '—'
);

// Computes the review-screen subtotal + unmatched-line count from items/matches
// — shared by StepReviewMatch's Summary panel and StepCustomerShipping's
// Document Summary panel so the two never compute this differently. Mirrors
// the per-line selection/price-fallback logic each line item's price card uses.
export const computeReviewTotals = (items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine) => {
  const matchesByItemKey = {};
  (matches || []).forEach((m) => {
    const key = String(m?.item_id ?? '');
    if (!key) return;
    (matchesByItemKey[key] = matchesByItemKey[key] || []).push(m);
  });

  let subtotal = 0;
  (items || []).forEach((item) => {
    const key = String(item.id || '');
    const itemMatches = matchesByItemKey[key] || [];
    const selectedCode = selectedByItem?.[key] || itemMatches[0]?.product_code;
    const selectedMatch = itemMatches.find((m) => m.product_code === selectedCode) || itemMatches[0] || null;
    const qty = Number(qtyByItem?.[key] ?? item?.normalized_parts?.qty ?? 1) || 1;
    const { extendedPrice } = resolveEffectiveLinePrice({
      matchUnitPrice: selectedMatch?.pricing?.unit_price,
      erpUnitPrice: erpPriceByProductCode?.[selectedCode],
      inputUnitPrice: item?.normalized_parts?.input_unit_price,
      inputExtendedPrice: item?.normalized_parts?.input_extended_price,
      pricingEngine,
      qty,
    });
    if (Number.isFinite(extendedPrice)) subtotal += extendedPrice;
  });

  const unmatchedCount = (items || []).filter(
    (item) => (matchesByItemKey[String(item.id || '')] || []).length === 0
  ).length;

  return { subtotal, unmatchedCount, matchesByItemKey };
};

// Groups review-screen items that share the backend-tagged canonical duplicate
// key (item.normalized_parts.duplicate_group_key). Items with no computable key
// (blank part number/description) are excluded. Only groups with 2+ members are
// returned — this is what drives the duplicate-lines banner in StepReviewMatch.
export const groupDuplicateItems = (items) => {
  const byKey = {};
  (items || []).forEach((item) => {
    const key = item?.normalized_parts?.duplicate_group_key;
    if (!key) return;
    (byKey[key] = byKey[key] || []).push(item);
  });
  return Object.entries(byKey)
    .filter(([, groupItems]) => groupItems.length > 1)
    .map(([key, groupItems]) => ({ key, items: groupItems }));
};

// The ten fields EditItemDetailsModal shows/edits — every field update_items
// accepts in normalized_parts except the system-managed `duplicate_group_key`.
export const EDITABLE_ITEM_FIELDS = [
  'part_number', 'manufacturer', 'manufacturer_part_number', 'customer_part_number',
  'description', 'qty', 'uom', 'notes', 'input_unit_price', 'input_extended_price',
];

// Builds the update_items payload item for a single line-item field edit
// (EditItemDetailsModal / OrderXV4.js's handleEditItemFields). update_items
// (order_genie_service.py:13571-13654) has no preserve-on-omit fallback for
// part_number/manufacturer_part_number/customer_part_number/manufacturer/
// description — and none at all for parsed_json — so omitting any of them
// from the payload would silently wipe that field server-side. This always
// sends all ten editable fields' current values (edited or not, sourced from
// `updatedFields` and falling back to the item's existing value for any the
// caller didn't supply), plus the item's existing, unmodified
// parsed_json/id/row_index, and preserves any other normalized_parts field
// (e.g. duplicate_group_key) untouched.
// See spec-orderxv4-review-edit-extracted-fields.md.
export const buildEditItemFieldsPayload = (item, updatedFields) => {
  const existing = item?.normalized_parts || {};
  const fieldValues = {};
  EDITABLE_ITEM_FIELDS.forEach((field) => {
    fieldValues[field] = Object.prototype.hasOwnProperty.call(updatedFields || {}, field)
      ? updatedFields[field]
      : existing[field];
  });
  return {
    id: item?.id,
    row_index: item?.row_index,
    parsed_json: item?.parsed_json,
    normalized_parts: { ...existing, ...fieldValues },
  };
};

// Builds the FULL items array update_items needs for a single-item field edit.
// update_items (order_genie_service.py:~13724) computes `kept_existing_ids`
// from the ids present in the incoming `items` array and DELETES every
// existing row (plus its matches and cart entries) whose id is NOT in that
// set -- it is a full-list sync, not a per-id targeted patch. Sending only
// the one edited item would silently delete every other line item in the
// request. Every other item must therefore be passed through unchanged
// (existing id/row_index/parsed_json/normalized_parts, untouched) alongside
// the edited one. See spec-orderxv4-review-edit-extracted-fields.md's
// Spec Change Log for the review finding this fixes.
export const buildFullItemsUpdatePayload = (items, editedItemId, updatedFields) => (items || []).map((item) => (
  String(item?.id ?? '') === String(editedItemId)
    ? buildEditItemFieldsPayload(item, updatedFields)
    : {
      id: item?.id,
      row_index: item?.row_index,
      parsed_json: item?.parsed_json,
      normalized_parts: item?.normalized_parts || {},
    }
));

// Client-side port of the backend's field-merge logic (richer value wins per
// field), but sums quantity instead of taking max() — see spec-orderxv4-duplicate-line-merge.
const MERGE_FIELDS = [
  'part_number', 'description', 'manufacturer',
  'manufacturer_part_number', 'customer_part_number', 'notes', 'uom',
];

export const mergeDuplicateGroup = (groupItems, qtyByItem) => {
  const merged = {};
  MERGE_FIELDS.forEach((field) => { merged[field] = ''; });
  let qty = 0;
  (groupItems || []).forEach((item) => {
    const parts = item?.normalized_parts || {};
    MERGE_FIELDS.forEach((field) => {
      const value = String(parts[field] || '').trim();
      if (value && value.length > merged[field].length) merged[field] = value;
    });
    const key = String(item.id || '');
    qty += Number(qtyByItem?.[key] ?? parts.qty ?? 1) || 1;
  });
  merged.qty = qty;
  return merged;
};

export const decodeHtmlEntities = (value) => {
  if (!value) return '';
  const el = document.createElement('textarea');
  el.innerHTML = String(value);
  return el.value;
};

// Shared "Export to Excel" for the Review & Match (step 2) and Summary & Submit
// (step 4) screens — both hold the same items/matches/selection shape, so the
// export columns (input vs. matched part identity + pricing) stay identical
// regardless of which step triggered the download.
const REVIEW_EXPORT_HEADERS = [
  'Row #',
  'Input Part Number',
  'Input Mfr Part #',
  'Input Description',
  'Matched Part Number',
  'Matched Mfr Part #',
  'Catalog Description',
  'Input Qty',
  'Input Price',
  'ERP Price',
  'Extended Price',
];

export const buildReviewExportRows = ({
  items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine,
}) => {
  const matchesByItemKey = {};
  (matches || []).forEach((m) => {
    const key = String(m?.item_id ?? '');
    if (!key) return;
    (matchesByItemKey[key] = matchesByItemKey[key] || []).push(m);
  });

  const rows = (items || []).map((item) => {
    const key = String(item.id || '');
    const itemMatches = matchesByItemKey[key] || [];
    const selectedCode = selectedByItem[key] || itemMatches[0]?.product_code;
    const selectedMatch = itemMatches.find((m) => m.product_code === selectedCode) || itemMatches[0] || null;
    const qty = Number(qtyByItem?.[key] ?? item?.normalized_parts?.qty ?? 1) || 1;
    const inputUnitPrice = Number(item?.normalized_parts?.input_unit_price);
    const erpUnitPrice = erpPriceByProductCode?.[selectedCode];
    const { extendedPrice } = resolveEffectiveLinePrice({
      matchUnitPrice: selectedMatch?.pricing?.unit_price,
      erpUnitPrice,
      inputUnitPrice,
      inputExtendedPrice: item?.normalized_parts?.input_extended_price,
      pricingEngine,
      qty,
    });

    return [
      Number.isFinite(Number(item?.row_index)) ? Number(item.row_index) + 1 : '',
      decodeHtmlEntities(item?.normalized_parts?.part_number || ''),
      decodeHtmlEntities(item?.normalized_parts?.manufacturer_part_number || ''),
      decodeHtmlEntities(item?.normalized_parts?.description || ''),
      selectedMatch?.product_code || '',
      decodeHtmlEntities(selectedMatch?.match?.manufacturerPartNumber || ''),
      decodeHtmlEntities(selectedMatch?.match?.description || selectedMatch?.match?.name || ''),
      qty,
      Number.isFinite(inputUnitPrice) ? inputUnitPrice : '',
      Number.isFinite(Number(erpUnitPrice)) ? Number(erpUnitPrice) : '',
      Number.isFinite(extendedPrice) ? Number(extendedPrice.toFixed(2)) : '',
    ];
  });

  return [REVIEW_EXPORT_HEADERS, ...rows];
};

export const exportReviewToExcel = ({
  items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine, fileNamePrefix,
}) => {
  const data = buildReviewExportRows({
    items, matches, selectedByItem, qtyByItem, erpPriceByProductCode, pricingEngine,
  });
  const ws = XLSX.utils.aoa_to_sheet(data);
  const colWidths = data[0].map((_, colIdx) => ({
    wch: Math.max(...data.map((row) => String(row[colIdx] ?? '').length), 10) + 2,
  }));
  ws['!cols'] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Line Items');
  const stamp = new Date().toISOString().slice(0, 10);
  XLSX.writeFile(wb, `${fileNamePrefix || 'order_review'}_${stamp}.xlsx`);
};

// Builds a displayable product image URL. Product records sometimes only carry a bare
// filename (e.g. "12345.jpg") rather than a full URL — in that case prefix it with the
// "Fallback Product Image Base URL" configured under AHRI Match Lookup settings.
// Mirrors buildProductImageUrl in frontend/src/pages/AhriSolr.js.
export const buildProductImageUrl = (raw, base) => {
  const src = String(raw || '').trim();
  if (!src) return '';
  if (/^https?:\/\//i.test(src) || src.startsWith('//') || src.startsWith('data:')) {
    return src;
  }
  const b = String(base || '').trim();
  if (!b) return src;
  const needsSlash = !b.endsWith('/');
  return `${b}${needsSlash ? '/' : ''}${src}`;
};

// Normalizes the varied shapes returned by POST /api/v1/orderx/search/products
// (top-level `results`/`docs`, or nested `result.docs`/`result.results`) into a
// flat array of product records with consistent field names.
export const extractOrderGenieProductSearchResults = (response) => {
  const topLevel = response && typeof response === 'object' ? response : {};
  const resultNode = topLevel?.result && typeof topLevel.result === 'object' ? topLevel.result : {};
  const candidates = [topLevel?.results, topLevel?.docs, resultNode?.docs, resultNode?.results];
  const docs = candidates.find((c) => Array.isArray(c)) || [];
  return docs.map((row, idx) => {
    const productCode = String(row?.product_code || row?.productCode || row?.part_number || '').trim();
    return {
      product_code: productCode,
      name: String(row?.name || row?.short_desc || '').trim(),
      description: String(row?.description || row?.long_desc || '').trim(),
      manufacturer_name: String(row?.manufacturer_name || row?.manufacturer || row?.manufacturerName || '').trim(),
      manufacturer_part_number: String(row?.manufacturer_part_number || row?.manufacturerPartNumber || '').trim(),
      image_url: String(row?.image_url || row?.imageUrl || '').trim(),
      _key: productCode || `row-${idx}`,
    };
  }).filter((p) => p.product_code);
};

// Normalizes POST /api/v1/orderx/search/customers' response
// ({ candidates: [...], total }) -- a clean shape already, unlike product
// search's legacy docs/numFound/result nesting. customer_id is the unique
// id per candidate (Story 1.1).
export const extractOrderGenieCustomerSearchResults = (response) => {
  const candidates = Array.isArray(response?.candidates) ? response.candidates : [];
  return candidates.map((row, idx) => {
    const customerId = String(row?.customer_id || '').trim();
    return {
      customer_id: customerId,
      name: String(row?.name || '').trim(),
      erp_customer_id: String(row?.erp_customer_id || '').trim(),
      external_customer_id: String(row?.external_customer_id || '').trim(),
      score: row?.score,
      _key: customerId || `row-${idx}`,
    };
  }).filter((c) => c.customer_id);
};

// Normalizes POST /api/v1/orderx/search/shipto's response
// ({ candidates: [...], total }). address_id is the unique id per candidate
// -- customer_id is only the OWNING customer and is shared across every
// address that customer has, so it must never be used as the key (Story 1.3,
// Task 0 -- a bug found and fixed in Story 1.2's search_shipto_addresses).
export const extractOrderGenieShipToSearchResults = (response) => {
  const candidates = Array.isArray(response?.candidates) ? response.candidates : [];
  return candidates.map((row, idx) => {
    const addressId = String(row?.address_id || '').trim();
    return {
      address_id: addressId,
      customer_id: String(row?.customer_id || '').trim(),
      name: String(row?.name || '').trim(),
      address_line1: String(row?.address_line1 || '').trim(),
      address_line2: String(row?.address_line2 || '').trim(),
      city: String(row?.city || '').trim(),
      state_province: String(row?.state_province || '').trim(),
      postal_code: String(row?.postal_code || '').trim(),
      country_code: String(row?.country_code || '').trim(),
      external_shipto_id: String(row?.external_shipto_id || '').trim(),
      score: row?.score,
      _key: addressId || `row-${idx}`,
    };
  }).filter((a) => a.address_id);
};
